import { useMutation, useQueryClient } from "react-query";
import { userGenerateDashboard } from "@/services/user";
import { useRouter } from "next/router";
import { AxiosError } from "axios";
import { cloneDeep } from "lodash";

import {
  useConfig,
  useEdit,
  useError,
  useGenerateResult,
} from "@/states/common.state";
import { useIsFormData, useIsloading } from "@/states/ui.state";
import { useHandleGenerateResult } from "@/hooks/useHandleGenerateResult";

const useGenerateQr = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { result } = useGenerateResult();
  const { handleGenerateResult } = useHandleGenerateResult();
  const { setConfig, setPayload } = useConfig();
  const { isEdit } = useEdit();
  const { setIsloading } = useIsloading();
  const { setError } = useError();
  const { isFormData } = useIsFormData();

  const checkPathname = (withConfig?: boolean) => {
    if (!!withConfig || router.pathname === "/dashboard/builder/steps/design") {
      return result.data.updateConfigUrl;
    }

    return isEdit ? result.data.updateConfigUrl : result.data.updateUrl;
  };

  const checkParams = (params: Record<string, any>) => {
    const newObj = cloneDeep(params);
    if (newObj?.config?.hasOwnProperty?.("withConfig")) {
      delete newObj.config?.["withConfig"];
    }

    return newObj;
  };

  const {
    mutate: setUserQrGenerateConfigUpdate,
    isLoading: isLoadingConfigUpdate,
    error,
    isError,
    isIdle,
  } = useMutation(
    (params: Record<string, any>) => {
      return userGenerateDashboard.postGenerateConfigUpdate(
        checkParams(params),
        checkPathname(params?.config?.withConfig),
        isFormData,
      );
    },
    {
      onSuccess: (data) => {
        if (!router.pathname.includes("dashboard")) {
          setConfig(data.data.config);
          setPayload(data.data.payload);
        }
        handleGenerateResult(data);
        setIsloading(false);
        isEdit && queryClient.invalidateQueries(["QR_LIST"]);
        isEdit && queryClient.invalidateQueries(["ANALYTICS_SHOW"]);
      },
      onError: ({ response }) => {
        setError(response);
        setIsloading(false);
      },
    },
  );

  return {
    setUserQrGenerateConfigUpdate,
    isLoadingConfigUpdate,
    error: error as AxiosError,
    isError,
    isIdle,
  };
};

export default useGenerateQr;
